<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref, watch } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, websiteVariants, seoApikomContent } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    seoApikomContent: {
        type: Object,
        required: false,
    },
});

const types = [
    {
        value: 'premium',
        label: 'Premium',
    },
    {
        value: 'standard',
        label: 'Standard',
    },
];

const groups = [
    {
        value: 'other',
        label: 'Autre',
    },
    {
        value: 'product',
        label: 'Produit',
    },
    {
        value: 'category',
        label: 'Catégorie',
    },
];

const { route } = useRoute();

const form = reactive({
    websiteVariant: websiteVariants.find(
        ({ id }) => id === seoApikomContent?.websiteVariantId,
    ),
    group: seoApikomContent
        ? seoApikomContent.cmsCategoryId
            ? groups[2]
            : seoApikomContent.cmsProductId
              ? groups[1]
              : groups[0]
        : groups[0],
    category:
        seoApikomContent && seoApikomContent.eCommerceCategory
            ? {
                  cmsCategoryId:
                      seoApikomContent.eCommerceCategory.cmsCategoryId,
                  languageName: seoApikomContent.eCommerceCategory.languageName,
                  languageUrl: seoApikomContent.eCommerceCategory.languageUrl,
                  seoContentCategory: seoApikomContent.eCommerceCategory
                      .seoContentCategory
                      ? {
                            contentType: {
                                value: seoApikomContent.eCommerceCategory
                                    .seoContentCategory.contentType.value,
                                label: seoApikomContent.eCommerceCategory
                                    .seoContentCategory.contentType.label,
                            },
                        }
                      : null,
              }
            : null,
    product:
        seoApikomContent && seoApikomContent.eCommerceProduct
            ? {
                  cmsProductId: seoApikomContent.eCommerceProduct.cmsProductId,
                  sku: seoApikomContent.eCommerceProduct.sku,
                  languageName: seoApikomContent.eCommerceProduct.languageName,
                  languageUrl: seoApikomContent.eCommerceProduct.languageUrl,
                  seoContentProduct: seoApikomContent.eCommerceProduct
                      .seoContentProduct
                      ? {
                            contentType: {
                                value: seoApikomContent.eCommerceProduct
                                    .seoContentProduct.contentType.value,
                                label: seoApikomContent.eCommerceProduct
                                    .seoContentProduct.contentType.label,
                            },
                        }
                      : null,
              }
            : null,
    url: seoApikomContent ? seoApikomContent.url : '',
    type: seoApikomContent
        ? types.find(({ value }) => value === seoApikomContent.type.value)
        : types[0],
    updatesDescription: seoApikomContent
        ? seoApikomContent.updatesDescription
        : true,
    updatesMeta: seoApikomContent ? seoApikomContent.updatesMeta : true,
    date: seoApikomContent ? seoApikomContent.date : '',
    comment: seoApikomContent ? seoApikomContent.comment : '',
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    group: object()
        .shape({
            value: string().required('Ce champ est obligatoire.'),
            label: string().required('Ce champ est obligatoire.'),
        })
        .required('Ce champ est obligatoire.'),
    category: object().when('group', {
        is: (value) => value.value === 'category',
        then: () => object().required('Ce champ est obligatoire.'),
        otherwise: () => object().nullable(),
    }),
    product: object().when('group', {
        is: (value) => value.value === 'product',
        then: () => object().required('Ce champ est obligatoire.'),
        otherwise: () => object().nullable(),
    }),
    url: string().required('Ce champ est obligatoire.'),
    type: object()
        .shape({
            value: string().required('Ce champ est obligatoire.'),
            label: string().required('Ce champ est obligatoire.'),
        })
        .required('Ce champ est obligatoire.'),
    updatesDescription: boolean(),
    updatesMeta: boolean(),
    date: string().nullable(),
    comment: string().nullable(),
});

const isSubmitting = ref(false);

const categories = ref([]);

const products = ref([]);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postApikomContent', {
                tenant: tenant.id,
                ...(seoApikomContent ? { id: seoApikomContent.id } : {}),
            }),
            {
                websiteVariantId: form.websiteVariant.id,
                cmsCategoryId: form.category
                    ? form.category.cmsCategoryId
                    : null,
                cmsProductId: form.product ? form.product.cmsProductId : null,
                url: form.url,
                type: form.type.value,
                updatesDescription: form.updatesDescription,
                updatesMeta: form.updatesMeta,
                date: form.date,
                comment: form.comment,
            },
        )
        .then(() => {
            toastSaved();

            goBack();
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.seo.apikomContents', {
                    tenant: tenant.id,
                }),
            );
        });
}

function updateCategories(event) {
    axios
        .post(
            route('front.projectManagement.seo.postApikomContentCategories', {
                tenant: tenant.id,
            }),
            {
                search: event.target.value,
            },
        )
        .then((response) => {
            categories.value = response.data;
        })
        .catch((error) => {
            console.error(error);
        });
}

function updateProducts(event) {
    axios
        .post(
            route('front.projectManagement.seo.postApikomContentProducts', {
                tenant: tenant.id,
            }),
            {
                search: event.target.value,
            },
        )
        .then((response) => {
            products.value = response.data;
        })
        .catch((error) => {
            console.error(error);
        });
}

function displayCategory(category) {
    return category
        ? 'ID' +
              ' : ' +
              category.cmsCategoryId +
              ' - ' +
              'Nom' +
              ' : ' +
              category.languageName
        : '';
}

function displayProduct(product) {
    return product
        ? 'ID' +
              ' : ' +
              product.cmsProductId +
              ' - ' +
              'SKU' +
              ' : ' +
              product.sku +
              ' - ' +
              'Nom' +
              ' : ' +
              product.languageName
        : '';
}

watch(
    () => form.group,
    () => {
        form.category = null;
        form.product = null;
        form.url = '';
    },
);

watch(
    () => form.category,
    () => {
        if (
            form.group.value === 'category' &&
            form.category &&
            form.category.languageUrl
        ) {
            form.url = form.category.languageUrl;
        }
    },
);

watch(
    () => form.product,
    () => {
        if (
            form.group.value === 'product' &&
            form.product &&
            form.product.languageUrl
        ) {
            form.url = form.product.languageUrl;
        }
    },
);
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    forceMount: true,
                    contentSlotName: 'information',
                },
                comment: {
                    title: 'Commentaire',
                    forceMount: true,
                    contentSlotName: 'comment',
                },
            }"
        >
            <template #information>
                <FormColumn>
                    <AlertBox
                        v-if="
                            (form.group.value === 'product' &&
                                form.product &&
                                form.product.seoContentProduct &&
                                form.product.seoContentProduct.contentType
                                    .value !== form.type.value) ||
                            (form.group.value === 'category' &&
                                form.category &&
                                form.category.seoContentCategory &&
                                form.category.seoContentCategory.contentType
                                    .value !== form.type.value)
                        "
                        type="danger"
                    >
                        Les types de contenu ne sont pas les mêmes.
                    </AlertBox>

                    <FormContent>
                        <FormGrid>
                            <ComboboxField
                                v-model="form.websiteVariant"
                                field-name="websiteVariant"
                                field-id="websiteVariant"
                                label="Site"
                                filtered-option-key="name"
                                :options="websiteVariants"
                                required
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid>
                            <ListboxField
                                v-model="form.group"
                                field-id="group"
                                field-name="group"
                                label="Groupe"
                                :options="groups"
                                by="value"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.group.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>

                        <FormGrid :columns="1">
                            <ComboboxField
                                v-if="form.group.value === 'category'"
                                v-model="form.category"
                                field-name="category"
                                field-id="category"
                                label="Catégorie"
                                filtered-option-key="cmsCategoryId"
                                :multiple-filtered-option-keys="[
                                    'cmsCategoryId',
                                    'languageName',
                                    'languageUrl',
                                ]"
                                :options="categories"
                                :display-value="displayCategory(form.category)"
                                @keyup="updateCategories"
                                required
                            >
                                <template #optionContent="{ option }">
                                    {{ displayCategory(option) }}
                                </template>
                            </ComboboxField>

                            <ComboboxField
                                v-if="form.group.value === 'product'"
                                v-model="form.product"
                                field-name="product"
                                field-id="product"
                                label="Produit"
                                filtered-option-key="cmsProductId"
                                :multiple-filtered-option-keys="[
                                    'cmsProductId',
                                    'sku',
                                    'languageName',
                                    'languageUrl',
                                ]"
                                :options="products"
                                :display-value="displayProduct(form.product)"
                                @keyup="updateProducts"
                                required
                            >
                                <template #optionContent="{ option }">
                                    {{ displayProduct(option) }}
                                </template>
                            </ComboboxField>
                        </FormGrid>

                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.url"
                                label="URL"
                                field-name="url"
                                field-id="url"
                                type="url"
                                required
                                :disabled="
                                    form.group.value === 'product' ||
                                    form.group.value === 'category'
                                "
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="types"
                                by="value"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <InputField
                                v-model="form.date"
                                label="Date"
                                field-name="date"
                                field-id="date"
                                type="month"
                            />
                        </FormGrid>

                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <SwitchField
                                v-model="form.updatesDescription"
                                label="Met à jour la description"
                                field-name="updatesDescription"
                                field-id="updatesDescription"
                            />

                            <SwitchField
                                v-model="form.updatesMeta"
                                label="Met à jour les meta"
                                field-name="updatesMeta"
                                field-id="updatesMeta"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #comment>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.comment"
                            label="Commentaire"
                            field-name="comment"
                            field-id="comment"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="seoApikomContent"
                    :url="
                        route(
                            'front.projectManagement.seo.deleteApikomContent',
                            {
                                tenant: tenant.id,
                                id: seoApikomContent.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.seo.apikomContents',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
